import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Camera, Globe, Megaphone } from "lucide-react";

const services = [
  {
    icon: <Camera className="w-8 h-8" />,
    title: "Contenido para Redes Sociales",
    description:
      "Creamos contenido visual atractivo y estratégico que fortalece la presencia digital de tu marca. Diseños que capturan la atención, generan engagement y construyen una comunidad fiel.",
    features: ["Diseño de posts", "Stories y Reels", "Calendarios de contenido", "Estrategia visual"],
    accent: "#FF6B6B",
  },
  {
    icon: <Globe className="w-8 h-8" />,
    title: "Diseño Web",
    description:
      "Desarrollamos sitios web modernos, funcionales y optimizados que transmiten confianza y convierten visitantes en clientes. Experiencias digitales que reflejan la esencia de tu marca.",
    features: ["Landing pages", "Sitios corporativos", "E-commerce", "Optimización SEO"],
    accent: "#D4AF37",
  },
  {
    icon: <Megaphone className="w-8 h-8" />,
    title: "Marketing Digital",
    description:
      "Estrategias integrales para impulsar la visibilidad, posicionamiento y conversión de tu negocio. Campañas que generan resultados medibles y un retorno real de inversión.",
    features: ["Publicidad pagada", "Email marketing", "Análisis y métricas", "Growth strategies"],
    accent: "#FF6B6B",
  },
];

export default function ServicesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section
      id="services"
      ref={ref}
      className="py-24 lg:py-32 bg-[#0a0a0a] relative"
      data-testid="services-section"
    >
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 lg:mb-20"
        >
          <p className="overline mb-4">Nuestros Servicios</p>
          <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-white mb-6">
            Soluciones que{" "}
            <span className="italic text-[#D4AF37]">impulsan</span> tu marca
          </h2>
          <p className="text-[#A3A3A3] max-w-2xl mx-auto text-sm lg:text-base">
            Ofrecemos servicios integrales de producción creativa y marketing digital
            diseñados para llevar tu negocio al siguiente nivel.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="group bg-[#121212] border border-white/10 rounded-lg p-6 lg:p-8 relative overflow-hidden hover:border-[#D4AF37]/30 transition-colors duration-200"
              data-testid={`service-card-${index}`}
            >
              <div className="relative z-10">
                {/* Icon */}
                <div
                  className="w-14 h-14 lg:w-16 lg:h-16 rounded-lg flex items-center justify-center mb-6"
                  style={{ backgroundColor: `${service.accent}15`, color: service.accent }}
                >
                  {service.icon}
                </div>

                {/* Title */}
                <h3 className="font-playfair text-xl lg:text-2xl font-bold text-white mb-4">
                  {service.title}
                </h3>

                {/* Description */}
                <p className="text-[#A3A3A3] text-sm leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* Features */}
                <div className="space-y-2">
                  {service.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-center gap-2">
                      <div
                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: service.accent }}
                      />
                      <span className="text-white/80 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
